import createCodingPersistence from './persistence'
import { requireCodingProfile, requireAnswerProfile, HTML_CSS_JS_PROFILE } from './project-profile'

const forms = new WeakMap()

// jquery.form has a synchronous veto hook before it serializes hidden inputs.
// This also catches synthetic jQuery submit events emitted by native Formlib.
export const registerCodingForm = (form, controller, jquery = window.jQuery) => {
  if (!form || !jquery) return () => {}
  let group = forms.get(form)
  if (!group) {
    group = { controllers: new Set(), flushing: false, permit: false }
    forms.set(form, group)
    const reset = () => {
      jquery(form).data('ajax_submitting', false).data('submitting', false).trigger('reset_submit')
      if (jquery.meLoadingBar) jquery.meLoadingBar.hide()
    }
    const flushThenSubmit = () => {
      if (group.flushing) return
      group.flushing = true
      const active = Array.from(group.controllers)
      active.forEach(item => item.setSubmitting(true))
      // Formlib finishes its synchronous bookkeeping before we reset the vetoed
      // request. No new request is issued until every coding answer is saved.
      Promise.resolve().then(reset).then(() => Promise.all(active.map(item => item.flush())))
        .then(() => {
          if (!active.every(item => item.isSettled())) throw new Error('Coding saves remain pending.')
          group.flushing = false
          group.permit = true
          jquery(form).trigger('submit')
          group.permit = false
        }).catch(() => {
          reset()
          group.flushing = false
          group.permit = false
          active.forEach(item => item.setSubmitting(false))
        })
    }
    const block = event => {
      event.preventDefault()
      event.stopImmediatePropagation()
      flushThenSubmit()
    }
    const nativeSubmit = event => {
      if (!group.permit && (group.flushing || !Array.from(group.controllers).every(item => item.isSettled()))) block(event)
    }
    form.addEventListener('submit', nativeSubmit, true)
    jquery(form).on('form-pre-serialize.codingQuestion', (event, _form, _options, veto) => {
      if (group.permit || (!group.flushing && Array.from(group.controllers).every(item => item.isSettled()))) return
      veto.veto = true
      event.preventDefault()
      flushThenSubmit()
    })
    jquery(form).on('formlib.ajax_error.codingQuestion', () => {
      Array.from(group.controllers).forEach(item => item.setSubmitting(false))
    })
    group.remove = () => {
      form.removeEventListener('submit', nativeSubmit, true)
      jquery(form).off('.codingQuestion')
      forms.delete(form)
    }
  }
  group.controllers.add(controller)
  return () => {
    group.controllers.delete(controller)
    if (!group.controllers.size) group.remove()
  }
}

export const mountCodingQuestion = async (host, suppliedConfig) => {
  if (!host || host.codingPersistence) return host && host.codingPersistence
  const config = suppliedConfig || JSON.parse(host.dataset.codingConfig || '{}')
  const element = host.querySelector('vapp-assessment-coding')
  const status = host.querySelector('[data-coding-status], .coding-question-status')
  const announce = message => {
    if (status) { status.hidden = false; status.textContent = message; status.setAttribute('role', 'alert') }
  }
  if (!element || !window.customElements) {
    announce('The coding project could not be loaded in this browser.')
    throw new Error('Coding component unavailable.')
  }
  let loadTimer
  try {
    await Promise.race([
      window.customElements.whenDefined('vapp-assessment-coding'),
      new Promise((_resolve, reject) => {
        loadTimer = window.setTimeout(() => reject(new Error('Coding component load timed out.')), 15000)
      }),
    ])
  } catch (error) {
    announce('The coding project could not be loaded. Reload before continuing.')
    throw error
  } finally { window.clearTimeout(loadTimer) }
  // Another caller may have mounted while both awaited the custom element.
  if (host.codingPersistence) return host.codingPersistence
  config.answerInput = host.querySelector('[data-coding-answer]')
  const recoveryButton = host.querySelector('[data-coding-preview-recover]')
  const supportsProfile = definition => requireCodingProfile(definition) === HTML_CSS_JS_PROFILE ||
    (typeof element.supportsCodingProfile === 'function' && element.supportsCodingProfile(definition) === true)
  try {
    requireCodingProfile(config.definition)
    if (config.answer) requireAnswerProfile(config.answer, config.definition)
    if (!supportsProfile(config.definition)) throw new Error('The coding component does not support this project.')
  } catch (error) {
    const message = 'This coding project is unsupported. Reload before continuing.'
    announce(message)
    element.attachHost({ definition: config.definition, readOnly: true,
      files: config.answer ? config.answer.files : config.definition?.starter,
      saveState: { status: 'error', message, readOnly: true } })
    // Keep the native submit veto even when no persistence controller can start.
    let disposed = false
    const blocked = { isSettled: () => false, flush: () => Promise.reject(error),
      setSubmitting: () => announce(message), dispose: () => {
        if (disposed) return
        disposed = true
        unregister()
        observer.disconnect()
      } }
    const unregister = config.preview ? () => {} : registerCodingForm(host.closest('form'), blocked)
    const observer = new MutationObserver(() => { if (!host.isConnected) blocked.dispose() })
    observer.observe(document.documentElement, { childList: true, subtree: true })
    host.codingPersistence = blocked
    return blocked
  }
  const controller = createCodingPersistence({
    config, supportsProfile,
    getFiles: () => element.files,
    setFiles: files => element.setFiles(files),
    onState: state => {
      element.setSaveState(state)
      // Native submit confirmation counts this marker. Initializing a starter
      // (revision zero), practice edits, or failed saves do not answer a question.
      // Accepted revisions only advance, so this state has no in-session reset.
      if (!config.preview && ['saved', 'readonly'].includes(state.status) &&
          [1, 2].includes(state.answer?.schemaVersion) && Number.isSafeInteger(state.answer.answerRevision) &&
          state.answer.answerRevision > 0) {
        host.closest('.activity-question-answer')?.classList.remove('activity-question-status-unanswered')
      }
      if (recoveryButton) recoveryButton.hidden = !state.localDraft
      // The workspace toolbar shows routine saves. Keep exceptional state in the
      // page's live region so recovery and save failures are announced as well.
      if (status) {
        const urgent = ['error', 'conflict', 'local-unavailable', 'terminal-local', 'terminal'].includes(state.status)
        const show = urgent || state.status === 'recovery'
        status.hidden = !show
        if (show) {
          status.textContent = state.message
          status.setAttribute('role', urgent ? 'alert' : 'status')
        }
      }
    },
  })
  element.definition = config.definition
  element.runnerOrigin = config.runnerOrigin || ''
  element.runtimeArtifactId = config.runtimeArtifactId || ''
  element.attachHost({
    definition: config.definition,
    runnerOrigin: config.runnerOrigin || '',
    runtimeArtifactId: config.runtimeArtifactId || '',
    preview: !!config.preview,
    files: config.answer ? config.answer.files : config.definition.starter,
    readOnly: !!config.readonly,
    saveState: { status: 'loading', message: 'Loading saved code…', readOnly: !config.preview || !!config.readonly },
    onFilesChange: () => controller.changed(),
    beforeRun: () => controller.beforeRun(),
    onHistoryAction: (operation, extras) => controller.operation(operation, extras),
    onRecoverLocalDraft: () => controller.recoverLocalDraft(),
    onInspectSource: (sourceId, sourceKind) => controller.inspectSource(sourceId, sourceKind),
    onRetrySave: () => controller.flush(),
  })
  const restorePreview = () => controller.recoverLocalDraft().catch(() => announce('The local practice draft could not be restored.'))
  if (recoveryButton) recoveryButton.addEventListener('click', restorePreview)
  const unregister = config.preview ? () => {} : registerCodingForm(host.closest('form'), controller)
  const beforeUnload = event => {
    if (!config.preview && !config.readonly && !controller.isSettled()) {
      event.preventDefault()
      event.returnValue = ''
    }
  }
  window.addEventListener('beforeunload', beforeUnload)
  const observer = new MutationObserver(() => {
    if (!host.isConnected) {
      controller.dispose()
      if (recoveryButton) recoveryButton.removeEventListener('click', restorePreview)
      unregister()
      window.removeEventListener('beforeunload', beforeUnload)
      observer.disconnect()
    }
  })
  observer.observe(document.documentElement, { childList: true, subtree: true })
  host.codingPersistence = controller
  try { await controller.initialize() } catch (error) {
    announce('Saved code could not be loaded. Retry or reload before continuing.')
  }
  return controller
}

export const startCodingQuestionHost = mountCodingQuestion
